import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  Card,
  Grid,
  Input,
  List,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";

import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { useSearchParams } from "react-router-dom";

import SaleForm from "../components/SaleForm";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { PageHeader } from "../../../shared/components/PageHeader";
import { formatApiTimestamp } from "../../../shared/utils/apiTimestamp";
import { appNotification } from "../../../shared/notifications/appNotification";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";

import {
  createSale as createLineBotUserRole,
  deleteSale as deleteLineBotUserRole,
  getSales as getLineBotUserRoles,
  updateSale as updateLineBotUserRole,
} from "../api/salesApi";

import type {
  SaleResponse as LineBotUserRole,
  SaleFormValues as LineBotUserRoleFormValues,
} from "../model/types";
import "../styles/Sales.css";

const { useBreakpoint } = Grid;

export default function Sales() {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();

  const isMobile = !screens.md;

  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LineBotUserRole[]>([]);
  const [total, setTotal] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LineBotUserRole>();
  const page = Number(searchParams.get("page") || 1);
  const pageSize = Number(searchParams.get("page_size") || 10);
  const search = searchParams.get("search") || "";
  const [searchText, setSearchText] = useState(search);

  const updateQuery = useCallback(
    (values: Record<string, any>) => {
      const params = new URLSearchParams(searchParams);

      Object.entries(values).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getLineBotUserRoles({
        page,
        page_size: pageSize,
        search: search || undefined,
      });

      setItems(response.items);
      setTotal(response.total);
    } catch (error) {
      appNotification.error({ message: "データの取得に失敗しました" });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: loadData });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadData]);

  useEffect(() => {
    setSearchText(search);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== search) {
        updateQuery({
          page: 1,
          search: searchText,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, search, updateQuery]);

  const handleCreate = () => {
    setSelectedItem(undefined);
    setFormOpen(true);
  };

  const handleEdit = (item: LineBotUserRole) => {
    setSelectedItem(item);
    setFormOpen(true);
  };

  const handleDelete = async (item: LineBotUserRole) => {
    try {
      await deleteLineBotUserRole(item.line_id);

      appNotification.success({ message: "削除しました" });

      loadData();
    } catch {
      appNotification.error({ message: "削除に失敗しました" });
    }
  };

  const confirmDelete = (item: LineBotUserRole) => {
    Modal.confirm({
      title: "削除してもよろしいですか？",
      content: "削除後は復元できず、一覧にも表示されません。",
      okText: "削除",
      cancelText: "キャンセル",
      okButtonProps: { danger: true },
      onOk: () => handleDelete(item),
    });
  };

  const handleSubmit = async (values: LineBotUserRoleFormValues) => {
    try {
      setLoading(true);

      if (selectedItem?.line_id) {
        await updateLineBotUserRole(selectedItem.line_id, values);

        appNotification.success({ message: "更新しました" });
      } else {
        await createLineBotUserRole(values);

        appNotification.success({ message: "作成しました" });
      }

      setFormOpen(false);

      loadData();
    } catch {
      appNotification.error({ message: "保存に失敗しました" });
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<LineBotUserRole> = [
    {
      title: "名前",
      dataIndex: "display_name",
      minWidth: 200,
      fixed: screens.lg ? "left" : undefined,
      ellipsis: {
        showTitle: true,
      },
      render: (value) => <Tooltip title={value}>{value ?? "--"}</Tooltip>,
    },
    {
      title: "LINEユーザーID",
      dataIndex: "line_id",
      minWidth: 200,
      ellipsis: {
        showTitle: true,
      },
      render: (value) => <Tooltip title={value}>{value ?? "--"}</Tooltip>,
    },
    //   {
    //     title: "Role",
    //     dataIndex: "role",
    //     render: (role: string) => (
    //       <Tag color={role === "sales" ? "blue" : "green"}>{role}</Tag>
    //     ),
    //   },
    {
      title: "メールアドレス",
      dataIndex: "email",
      minWidth: 200,
      ellipsis: {
        showTitle: true,
      },
      render: (value) => <Tooltip title={value}>{value ?? "--"}</Tooltip>,
    },
    // {
    //   title: "電話番号",
    //   dataIndex: "phone_number",
    //   minWidth: 300,
    //   ellipsis: {
    //     showTitle: true,
    //   },
    //   render: (value) => <Tooltip title={value}>{value ?? "--"}</Tooltip>,
    // },
    {
      title: "操作",
      width: 180,
      fixed: screens.lg ? "right" : undefined,
      align: "center",
      render: (_: any, record: LineBotUserRole) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          {/* <Button
              icon={<EyeOutlined />}
              onClick={() => handleDetail(record)}
            /> */}

          <Button
            type="primary"
            style={{ borderRadius: 50 }}
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />

          <Button
            danger
            style={{ borderRadius: 50 }}
            icon={<DeleteOutlined />}
            onClick={() => confirmDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="sales-page">
        <PageHeader
          title="営業担当者管理"
          description="営業担当者のLINEアカウントとメールアドレスを管理します。"
          actionLabel="新規営業担当者登録"
          onAction={handleCreate}
        />
        <Card className="sales-list-card" bordered={false}>
          <div className="sales-list-toolbar">
            <Input
              allowClear
              size="large"
              prefix={<SearchOutlined />}
              placeholder="名前・メールアドレス・電話番号で検索"
              value={searchText}
              style={{ width: isMobile ? "100%" : 600 }}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {!isMobile && (
              <Button
                size="large"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => void loadData()}
              >
                更新
              </Button>
            )}
          </div>

          {isMobile ? (
            <>
              <List
                className="sales-mobile-list"
                loading={loading}
                dataSource={items}
                pagination={false}
                renderItem={(item) => (
                  <List.Item className="sales-mobile-list-item">
                    <Card className="sales-mobile-card" size="small">
                      <div className="sales-mobile-card-content">
                        <div className="sales-mobile-card-header">
                          <Typography.Text
                            strong
                            className="sales-mobile-card-title"
                          >
                            {item.display_name || "---"}
                          </Typography.Text>

                          <Tag color={item.role === "sale" ? "blue" : "green"}>
                            {item.role === "sale" ? "営業担当者" : "お客様"}
                          </Tag>
                        </div>

                        <div className="sales-mobile-card-details">
                          <Typography.Text strong>
                            LINEユーザーID: {item.line_id}
                          </Typography.Text>
                          <Typography.Text className="sales-mobile-card-email">
                            {item.email || "---"}
                          </Typography.Text>
                          <Typography.Text type="secondary">
                            更新日時:{" "}
                            {item.updated_at
                              ? formatApiTimestamp(item.updated_at)
                              : "---"}
                          </Typography.Text>
                        </div>

                        <div className="sales-mobile-card-actions">
                          <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(item)}
                          >
                            編集
                          </Button>

                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => confirmDelete(item)}
                          >
                            削除
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
              <MobilePagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={(current) => updateQuery({ page: current })}
              />
            </>
          ) : (
            <Table
              rowKey="line_id"
              loading={loading}
              columns={columns}
              dataSource={items}
              scroll={{ x: 800 }}
              tableLayout="fixed"
              pagination={{
                current: page,
                pageSize,
                total,
                size: "middle",
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (total, range) =>
                  `${total} 件中 ${range[0]}-${range[1]} を表示`,
                onChange: (current, size) =>
                  updateQuery({
                    page: size !== pageSize ? 1 : current,
                    page_size: size,
                  }),
              }}
            />
          )}
        </Card>
      </div>

      <SaleForm
        open={formOpen}
        loading={loading}
        readOnly={false}
        initialValues={selectedItem}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
}
